import { Link as RemixLink, useRouteLoaderData } from "@remix-run/react";
import { Button, Text, Link } from "@chakra-ui/react";

import { useUserSubscriptionValue } from "#app/hooks/index.js";
import { ActiveRequestHelpPopover } from "#app/components/index.jsx";
import { ManageSubscriptionButton } from "#app/components/buttons/index.jsx";

import { getDisplayPrice, plans } from "#app/config/stripe.js";
import { CheckoutButton } from "../../buttons/CheckoutButton.jsx";
import { PricingCard } from "./PricingCard.jsx";
import { actionButtonProps } from "./shared.jsx";

export function BasicPricingCard({
  name = "Basic",
  colorScheme = "gray",
  ...props
} = {}) {
  const { pricingCurrency } = useRouteLoaderData("root");
  const price = getDisplayPrice("basic", pricingCurrency);

  const badgeText = useUserSubscriptionValue({
    none: () => null,
    trial: () => null,
    active: () => null,
    active_basic: () => "your plan",
  });

  const subscribeButton = (
    <CheckoutButton
      plan={name}
      colorScheme={colorScheme}
      {...actionButtonProps}
    >
      Subscribe to {name}
    </CheckoutButton>
  );

  const actionButton = useUserSubscriptionValue({
    none: () => (
      <Button isDisabled colorScheme={colorScheme} {...actionButtonProps}>
        Subscribe to {name}
      </Button>
    ),
    trial: () => subscribeButton,
    active_student: () => subscribeButton,
    active_basic: () => (
      <Button colorScheme={colorScheme} isDisabled {...actionButtonProps}>
        Subscribe to {name}
      </Button>
    ),
    active: () => (
      <ManageSubscriptionButton
        colorScheme={colorScheme}
        {...actionButtonProps}
      >
        Downgrade to {name}
      </ManageSubscriptionButton>
    ),
  });

  const actionButtonHelperText = useUserSubscriptionValue({
    none: () => (
      <>
        {"You must "}
        <Link as={RemixLink} to="/login">
          log in
        </Link>
        {" to subscribe"}
      </>
    ),
    trial: () => "Checkout with Stripe",
    active_student: () => "Checkout with Stripe",
    active: () => "Manage with Stripe",
    active_basic: () => (
      <>
        {"You're subscribed to this plan. "}
        <ManageSubscriptionButton variant="link">
          Manage
        </ManageSubscriptionButton>
      </>
    ),
  });

  return (
    <PricingCard
      badgeText={badgeText}
      colorScheme={colorScheme}
      name={name}
      price={price}
      pricingCurrency={pricingCurrency}
      duration="/ mo"
      description="Unlimited notifications. Includes text messages."
      features={[
        "Unlimited notifications",
        "Email and Text",
        <Text>
          {plans.basic.maxActiveRequests} <ActiveRequestHelpPopover />
        </Text>,
        "Checks every 1-5 minutes",
      ]}
      actionButton={actionButton}
      actionButtonHelperText={actionButtonHelperText}
      {...props}
    />
  );
}
